import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { useUser } from '../context/UserContext';
import { useToast } from '../hooks/use-toast';
import usePageSEO from '../hooks/usePageSEO';
import { isStrongPassword } from '../lib/password';

const ResetPassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { resetPassword } = useUser();
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ password: '', confirmPassword: '' });
  const token = searchParams.get('token') || '';

  usePageSEO({ title: t('auth.resetPassword'), noIndex: true });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast({ title: t('auth.error'), description: t('auth.resetMissingToken'), variant: 'destructive' });
      return;
    }
    if (form.password !== form.confirmPassword) {
      toast({ title: t('auth.error'), description: t('auth.passwordsDoNotMatch'), variant: 'destructive' });
      return;
    }
    if (!isStrongPassword(form.password)) {
      toast({ title: t('auth.error'), description: t('auth.passwordComplexity'), variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      await resetPassword(token, form.password);
      toast({ title: t('auth.passwordReset'), description: t('auth.passwordResetDesc') });
      navigate('/login');
    } catch (error) {
      const msg = error.response?.data?.detail || t('auth.unableToResetPassword');
      toast({ title: t('auth.error'), description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pt-24 pb-16 flex items-center justify-center bg-gray-50" data-testid="reset-password-page">
      <Card className="w-full max-w-md mx-4 shadow-xl">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">{t('auth.resetPassword')}</CardTitle>
          <p className="text-gray-500 text-sm mt-1">{t('auth.resetPasswordDesc')}</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>{t('auth.newPassword')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input data-testid="reset-password-new" type={showPassword ? 'text' : 'password'} className="pl-10 pr-10" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required placeholder={t('auth.passwordMinLength')} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label>{t('auth.confirmPassword')}</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input data-testid="reset-password-confirm" type="password" className="pl-10" value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })} required placeholder={t('auth.repeatPassword')} />
              </div>
            </div>
            <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={loading} data-testid="reset-password-submit">
              {loading ? t('auth.resettingPassword') : t('auth.resetPasswordBtn')}
            </Button>
          </form>
          <p className="text-center text-sm text-gray-500 mt-6">
            <Link to="/login" className="text-red-600 font-semibold hover:underline">{t('auth.backToSignIn')}</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPassword;
